import { useEffect, useRef, useState } from 'react';

// Turns wheel, touch and keyboard input into progress through the corridor,
// without scrolling the page underneath.
export function useVentureTravel(active: boolean, onComplete: () => void) {
  const [progress, setProgress] = useState(0);
  const value = useRef(0);
  const done = useRef(false);
  const completeRef = useRef(onComplete);
  completeRef.current = onComplete;

  useEffect(() => {
    if (!active) return;

    // Publish progress at most once per frame, so wheel bursts never stall rendering
    let raf = 0;
    const publish = () => {
      raf = 0;
      setProgress(value.current);
    };

    const advance = (delta: number) => {
      if (done.current) return;
      value.current = Math.min(1, Math.max(0, value.current + delta));
      if (!raf) raf = requestAnimationFrame(publish);
      if (value.current >= 1) {
        done.current = true;
        completeRef.current();
      }
    };

    const onWheel = (e: WheelEvent) => {
      e.preventDefault();
      const dy = e.deltaMode === 1 ? e.deltaY * 16 : e.deltaY;
      // Clamp big mouse-wheel ticks so travel stays even between trackpads and wheels.
      // A slow rate leaves time to linger in each environment.
      advance(Math.max(-50, Math.min(50, dy)) * 0.00008);
    };

    let lastY = 0;
    const onTouchStart = (e: TouchEvent) => {
      lastY = e.touches[0].clientY;
    };
    const onTouchMove = (e: TouchEvent) => {
      e.preventDefault();
      const y = e.touches[0].clientY;
      advance((lastY - y) * 0.0004);
      lastY = y;
    };

    const onKey = (e: KeyboardEvent) => {
      if (['ArrowDown', 'PageDown', ' '].includes(e.key)) {
        e.preventDefault();
        advance(0.025);
      } else if (['ArrowUp', 'PageUp'].includes(e.key)) {
        e.preventDefault();
        advance(-0.025);
      }
    };

    window.addEventListener('wheel', onWheel, { passive: false });
    window.addEventListener('touchstart', onTouchStart, { passive: true });
    window.addEventListener('touchmove', onTouchMove, { passive: false });
    window.addEventListener('keydown', onKey);
    return () => {
      if (raf) cancelAnimationFrame(raf);
      window.removeEventListener('wheel', onWheel);
      window.removeEventListener('touchstart', onTouchStart);
      window.removeEventListener('touchmove', onTouchMove);
      window.removeEventListener('keydown', onKey);
    };
  }, [active]);

  return progress;
}
